import React, {useEffect, useState} from "react";
import jetBeansLogo from "../images/jb_beam.svg";
import vscodeLogo from "../images/vscode.svg";
import "yet-another-react-lightbox/styles.css";
import {PlansTable} from "./PlansPage";
import './Frontpage.css';
import {FrontpageFeatures} from "../components/Features";
import {FrontpageTestimonials} from "../components/Testimonials";
import {Helmet} from "react-helmet-async";

export default function Frontpage() {
    const title = "Codebuddy: AI Coding Assistant for VS Code & JetBrains | Multi-File Editing"
    const description = "Codebuddy is an AI coding assistant with multi-file editing, deep codebase understanding, and voice coding for VS Code and JetBrains. Chat with your codebase using Claude, GPT, and Gemini models. Usage-based pricing."

    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Organization",
                "name": "Codebuddy AI Inc.",
                "url": "https://www.codebuddy.ca",
                "logo": "https://storage.googleapis.com/aicoder-resources/images/logo2c-codebuddy-1110px-square.png",
                "sameAs": [
                    "https://discord.gg/Qa9vYdE78a",
                    "https://www.youtube.com/@CodebuddyAI",
                    "https://twitter.com/CodebuddyAI",
                    "https://marketplace.visualstudio.com/items?itemName=CodebuddyAI.codebuddy-ai",
                    "https://plugins.jetbrains.com/plugin/22666-codebuddy"
                ]
            },
            {
                "@type": "SoftwareApplication",
                "name": "Codebuddy",
                "applicationCategory": "DeveloperApplication",
                "operatingSystem": "Windows, macOS, Linux",
                "description": description,
                "url": "https://www.codebuddy.ca",
                "offers": {
                    "@type": "AggregateOffer",
                    "priceCurrency": "USD",
                    "lowPrice": "0",
                    "offerCount": "2",
                    "offers": [
                        {
                            "@type": "Offer",
                            "name": "BYOK Personal",
                            "price": "0",
                            "priceCurrency": "USD",
                            "description": "Bring your own OpenAI key, free for personal projects; add an Anthropic or OpenRouter key to cover more models"
                        },
                        {
                            "@type": "Offer",
                            "name": "Enterprise",
                            "price": "0",
                            "priceCurrency": "USD",
                            "description": "Custom pricing with on-site or cloud deployment"
                        }
                    ]
                }
            },
            {
                "@type": "FAQPage",
                "mainEntity": [
                    {
                        "@type": "Question",
                        "name": "What IDEs does Codebuddy support?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Codebuddy has native plugins for both VS Code and JetBrains IDEs (IntelliJ IDEA, PyCharm, WebStorm, and more)."
                        }
                    },
                    {
                        "@type": "Question",
                        "name": "What AI models does Codebuddy support?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Codebuddy supports frontier models from Anthropic, OpenAI and Google — including Claude Opus 5, Claude Sonnet 5, the GPT 5.6 family and Gemini 3.5 — plus models from DeepSeek, xAI, Moonshot and Z.ai. The current list and per-model credit costs are shown on the pricing page."
                        }
                    },
                    {
                        "@type": "Question",
                        "name": "Can I use my own API key with Codebuddy?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Yes. An OpenAI key is the baseline and covers the GPT models. Adding an OpenRouter key also covers Gemini, Grok, DeepSeek, GLM and Kimi, and adding an Anthropic key covers Claude. Any model your keys do not cover is billed with credits."
                        }
                    },
                    {
                        "@type": "Question",
                        "name": "Does Codebuddy support multi-file editing?",
                        "acceptedAnswer": {
                            "@type": "Answer",
                            "text": "Yes. Codebuddy can generate new files and modify multiple existing files from a single prompt using a unified diff format. This is a key differentiator from tools like GitHub Copilot."
                        }
                    }
                ]
            }
        ]
    }

    return (
        <>
            <Helmet prioritizeSeoTags>
                <title>{title}</title>
                <link rel="canonical" key="link-canonical" href="https://www.codebuddy.ca/"/>
                <meta property="og:title" name="og:title" key="og:title" content={title}/>
                <meta property="twitter:title" name="twitter:title" key="twitter:title" content={title}/>
                <meta property="description" name="description" key="description" content={description}/>
                <meta property="og:description" name="og:description" key="og:description" content={description}/>
                <meta property="twitter:description" name="twitter:description" key="twitter:description" content={description}/>
                <meta property="og:url" name="og:url" key="og:url" content="https://www.codebuddy.ca/"/>
                <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
            </Helmet>
            <FrontpageHead/>
            <FrontpageFeatures/>
            <FrontpageTestimonials/>
            <FrontpagePricing/>
        </>
    )
}

function isMobile(): boolean {
    return window.matchMedia("(max-width: 600px)").matches;
}

function FrontpageHead() {
    const options = ["JAVA", "JS", "KOTLIN", "C++", "C#", "PYTHON", "PHP", "RUBY", "GO", "RUST", "TS", "HTML", "CSS", "SQL", "BASH", "MD", "LATEX", "SCSS", "LESS", "VUE", "SVELTE", "ANGULAR", "REACT", "EMBER", "ZIG"];
    const [currentOption, setCurrentOption] = useState(0);

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentOption((prevOption) => (prevOption + 1) % options.length);
        }, 800);
        return () => clearInterval(interval);
    }, [options.length]);


    return (
        <div className="section gradient__01">
            <div className="margin-top margin-9xl">
                <div className="container-lg">
                    <div className="w-layout-grid hero-grid-halves">
                        <div className="content-centered-tablet">
                            <div className="margin-bottom margin-3xl">
                                <div className="margin-bottom margin-2xl">
                                    <h3 className="title-md">AI Powered Coding<br/>
                                        <span className="text-gradient__indigo-red">Perfect for </span>
                                        <span
                                            className={`text-gradient__indigo-red perfect-for-anim`}>{options[currentOption]}</span>
                                    </h3>
                                </div>
                            </div>
                            <div className="w-layout-grid column-base">
                                <div className="w-layout-grid row-base column-tablet">
                                    <div>Chat about your codebase and let your AI code assistant <br/>
                                        update the multiple files right in your favorite IDE!
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div className="max-width-xl content-left">
                                <div style={{paddingTop: "56.17021276595745%"}}
                                     className="w-embed-youtubevideo">
                                    <MainYoutubeVideo/>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="w-layout-blockcontainer container w-container">
                        <JetbrainsButton/>
                        <VSCodeButton/>
                    </div>
                </div>
            </div>
        </div>
    )
}

export function JetbrainsButton() {
    return (
        <a href="https://plugins.jetbrains.com/plugin/22666-codebuddy"
           className="button-animated large w-inline-block download-button">
            <img src={jetBeansLogo} loading="lazy" width="50" alt="Jetbrains Icon"
                 className="image-11"/>
            <div className="button-label">Jetbrains Plugin</div>
            <div className="button-hover-background"></div>
        </a>
    )
}

function MainYoutubeVideo() {
    const [open, setOpen] = useState(false);
    if (open || !isMobile()) {
        return (
            <iframe
                src="https://www.youtube-nocookie.com/embed/bdULrd276LY?rel=0&amp;controls=1&amp;autoplay=1&amp;mute=1&amp;start=0"
                style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: "100%",
                    height: "100%",
                    pointerEvents: "auto",
                    border: "none"
                }} allow="autoplay; encrypted-media" allowFullScreen={true}
                title="Codebuddy Intro and Birthday Feature Demonstrator"></iframe>
        )
    } else {
        return (
            <img className="main-youtube-thumbnail" src="/images/codebuddy-video-play-image.jpg" alt="Codebuddy Overview" onClick={() => setOpen(true)}/>
        )
    }
}

export function VSCodeButton() {
    return (
        <a href="https://marketplace.visualstudio.com/items?itemName=CodebuddyAI.codebuddy-ai"
           className="button-animated large w-inline-block download-button">
            <img src={vscodeLogo} loading="lazy" width="50" alt="VS Code Icon"
                 className="image-11"/>
            <div className="button-label">VSCode Extension</div>
            <div className="button-hover-background"></div>
        </a>
    )
}

function FrontpagePricing() {
    return (
        <section id="pricing" className="section padding-bottom">
            <PlansTable includeUserData={false}/>
        </section>
    )
}
